/**
 * Key points of a transform grid: the bounding box of its entities and the
 * anchors handles are placed on.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { KeyPoints } from "./types";
import { deleteObj } from "../core/delete";

/**
 * Bounding box of all `entityIds` in world coordinates, or null when it cannot
 * be computed (no entity, empty extents).
 */
export function computeKeyPoints(
  module: DrawingModuleType,
  entityIds: DrawingModule.OdDbObjectId[],
): KeyPoints | null {
  if (entityIds.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const entityId of entityIds) {
    const entity = entityId.safeOpenObject(
      0,
      false,
    ) as DrawingModule.OdDbEntity;
    const extents = new module.OdGeExtents3d();
    entity.getGeomExtents(extents);

    const min = extents.minPoint();
    const max = extents.maxPoint();

    minX = Math.min(minX, min.x);
    minY = Math.min(minY, min.y);
    maxX = Math.max(maxX, max.x);
    maxY = Math.max(maxY, max.y);

    deleteObj(min);
    deleteObj(max);
    deleteObj(extents);
    deleteObj(entity);
  }

  if (
    !isFinite(minX) ||
    !isFinite(minY) ||
    !isFinite(maxX) ||
    !isFinite(maxY)
  ) {
    return null;
  }

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return {
    center: { x: centerX, y: centerY, z: 0 },
    topLeft: { x: minX, y: maxY, z: 0 },
    topRight: { x: maxX, y: maxY, z: 0 },
    bottomLeft: { x: minX, y: minY, z: 0 },
    bottomRight: { x: maxX, y: minY, z: 0 },
    topMiddle: { x: centerX, y: maxY, z: 0 },
    bottomMiddle: { x: centerX, y: minY, z: 0 },
    leftMiddle: { x: minX, y: centerY, z: 0 },
    rightMiddle: { x: maxX, y: centerY, z: 0 },
    min: { x: minX, y: minY, z: 0 },
    max: { x: maxX, y: maxY, z: 0 },
  };
}
